import React, { useState } from 'react';
import { Award, ChevronDown, ChevronUp, MessageSquare, Star, CheckCircle2 } from 'lucide-react';

const OfficialMetricsCard = ({ evaluation, noBorder = false, hideReasoning = false }) => {
  const [isReasoningExpanded, setIsReasoningExpanded] = useState(false);

  if (!evaluation) return null;

  const { reasoning, scores } = evaluation;

  const metrics = [
    { key: 'clarity_coherence', label: 'Clarity & Coherence', description: 'Clear, concise, and readable' },
    { key: 'spoilers', label: 'Spoilers', description: 'No major spoilers revealed' },
    { key: 'hook', label: 'Hook', description: 'Starts with conflict/question' },
    { key: 'tone_genre_match', label: 'Tone-Genre Match', description: 'Vocabulary matches genre' },
    { key: 'irony_of_situation', label: 'Irony of Situation', description: 'Highlights a contradiction' },
    { key: 'the_or_else', label: 'The "Or Else"', description: 'Consequence of failure clear' },
    { key: 'length', label: 'Length', description: 'Fits the ideal length range' },
    { key: 'core_narrative_elements', label: 'Core Narrative Elements', description: 'Setting, characters, conflict' },
    { key: 'faithfulness', label: 'Faithfulness', description: 'Accurately reflects real plot' },
  ];

  // Calculate average for display
  const scoreValues = Object.values(scores);
  const averageScore = scoreValues.length > 0 
    ? scoreValues.reduce((a, b) => a + b, 0) / scoreValues.length 
    : 0;

  const getScoreColor = (score) => {
    if (score >= 4.5) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 3.5) return 'text-sky-600 dark:text-sky-400';
    if (score >= 2.5) return 'text-amber-600 dark:text-amber-400';
    return 'text-rose-600 dark:text-rose-400';
  };

  const getProgressColor = (score) => {
    if (score >= 4.5) return 'bg-emerald-500';
    if (score >= 3.5) return 'bg-sky-500';
    if (score >= 2.5) return 'bg-amber-500';
    return 'bg-rose-500';
  };

  return (
    <div className={`bg-white dark:bg-neutral-950 rounded-lg ${noBorder ? '' : 'border border-gray-200 dark:border-neutral-800 shadow-sm'} overflow-hidden`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-neutral-800">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-md font-semibold text-gray-900 dark:text-neutral-100 flex items-center">
            <Award className="w-5 h-5 mr-2 text-indigo-600 dark:text-indigo-400" />
            Official Evaluation Metrics
          </h2>
          <div className="flex items-center space-x-1 bg-indigo-100 dark:bg-indigo-900/40 px-2 py-0.5 rounded-full border border-indigo-200 dark:border-indigo-800">
            <Star className="w-3 h-3 text-indigo-600 dark:text-indigo-400 fill-current" />
            <span className="text-xs font-bold text-indigo-700 dark:text-indigo-300">
              {averageScore.toFixed(1)}/5.0
            </span>
          </div>
        </div>
        <p className="text-[10px] text-indigo-600/70 dark:text-indigo-400/70 uppercase font-bold tracking-wider">
          Expert Story Editor Pipeline
        </p>
      </div>

      <div className="p-4 space-y-5">
        {/* Metrics Grid */}
        <div className="grid grid-cols-1 gap-4">
          {metrics.map((metric) => {
            const score = scores[metric.key] || 0;
            return (
              <div key={metric.key} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold text-gray-700 dark:text-neutral-300">{metric.label}</span>
                    <p className="text-[9px] text-gray-400 leading-none">{metric.description}</p>
                  </div>
                  <span className={`text-xs font-black ${getScoreColor(score)}`}>
                    {score.toFixed(1)}
                  </span>
                </div>
                <div className="h-1.5 w-full bg-gray-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${getProgressColor(score)} transition-all duration-1000 ease-out`}
                    style={{ width: `${(score / 5) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Reasoning Section */}
        {!hideReasoning && (
          <div className="pt-2 border-t border-gray-100 dark:border-neutral-800">
            <button 
              onClick={() => setIsReasoningExpanded(!isReasoningExpanded)}
              className="flex items-center justify-between w-full text-xs font-bold text-gray-500 dark:text-neutral-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              <div className="flex items-center">
                <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                <span>STORY EDITOR'S REASONING</span>
              </div>
              {isReasoningExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {isReasoningExpanded && (
              <div className="mt-3 bg-gray-50 dark:bg-neutral-900 rounded-lg p-3 border border-gray-100 dark:border-neutral-800 animate-in slide-in-from-top-2 duration-200">
                <p className="text-[11px] text-gray-600 dark:text-neutral-400 whitespace-pre-wrap leading-relaxed italic">
                  {reasoning}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Evaluation Insight */}
        <div className="mt-4 p-2 bg-indigo-50/50 dark:bg-indigo-900/5 border border-indigo-100 dark:border-indigo-900/30 rounded-lg">
          <div className="flex items-start space-x-3">
            <CheckCircle2 className="w-5 h-5 text-indigo-500 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-indigo-900 dark:text-indigo-300">Evaluation Insight</h4>
              <p className="text-xs text-indigo-700/70 dark:text-indigo-400/70 leading-relaxed mt-1">
                These metrics are generated by an expert Story Editor pipeline using Chain-of-Thought reasoning. 
                A score above 4.0 indicates exceptional quality according to the official 9-point rubric.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfficialMetricsCard;
